import { ct } from './i18n.js';

/** Typed DOM utilities, to avoid `as HTMLInputElement` scattered everywhere. */

export function $<T extends Element = HTMLElement>(
  selector: string,
  root: ParentNode = document,
): T | null {
  return root.querySelector<T>(selector);
}

export function $$<T extends Element = HTMLElement>(
  selector: string,
  root: ParentNode = document,
): T[] {
  return [...root.querySelectorAll<T>(selector)];
}

/** Like `$`, but throws when the element is absent: signals a bug in a view. */
export function must<T extends Element = HTMLElement>(
  selector: string,
  root: ParentNode = document,
): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Élément introuvable : ${selector}`);
  return element;
}

/** Reads the `<script type="application/json" id="page-data">` block injected by the view. */
export function pageData<T>(): T {
  const element = document.getElementById('page-data');
  if (!element?.textContent) {
    throw new Error('Données de page absentes (#page-data).');
  }
  return JSON.parse(element.textContent) as T;
}

/** Enables or disables a button and its loading indicator. */
export function setLoading(button: HTMLButtonElement | null, loading: boolean): void {
  if (!button) return;
  button.disabled = loading;
  const spinner = button.querySelector('.loading');
  spinner?.classList.toggle('hidden', !loading);
}

/**
 * Creates an element with its classes, text and attributes.
 *
 * `style` is a separate option, and `attrs.style` is a type error. Passing the
 * declaration as an attribute went through `setAttribute`, which the CSP blocks
 * exactly like a `style="…"` written in a template — so the role colour dots
 * were never painted, silently, in the chips and in the dropdown alike. Going
 * through the CSSOM is what the policy allows, and a property record leaves no
 * room for a declaration smuggled inside a string.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: {
    class?: string;
    text?: string;
    attrs?: Record<string, string> & { style?: never };
    style?: Partial<Record<'backgroundColor' | 'color' | 'width' | 'borderLeftColor', string>>;
  } = {},
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (options.class) element.className = options.class;
  // `textContent` and not `innerHTML`: no server data is interpreted as HTML
  // on the client side.
  if (options.text !== undefined) element.textContent = options.text;
  for (const [key, value] of Object.entries(options.attrs ?? {})) {
    element.setAttribute(key, value);
  }
  for (const [property, value] of Object.entries(options.style ?? {})) {
    if (value) element.style[property as 'backgroundColor'] = value;
  }
  return element;
}

/**
 * The "this role sits above the bot" warning badge.
 *
 * Built identically in the role picker and in the select menu, down to the
 * translation key and its fallback - only the sizing classes differed. Two
 * copies of a warning is how one of them ends up saying something the other
 * does not.
 */
export function aboveBotBadge(extraClass = ''): HTMLSpanElement {
  return el('span', {
    class: `text-warning ${extraClass}`.trim(),
    text: '⚠',
    attrs: { title: ct('roleAboveBot', 'This role sits above the bot and cannot be granted.') },
  });
}

/**
 * Applies the styles the server could not put in a `style` attribute.
 *
 * The Content-Security-Policy forbids inline styles, so a `style="width: 73%"`
 * rendered server-side is blocked outright - the quota bars showed no fill and
 * the embed previews no colour, silently, on every page load. Setting the same
 * property through the CSSOM is what the policy allows: the script is `'self'`,
 * and the value never becomes CSS text.
 *
 * Deliberately two named properties rather than a generic "apply arbitrary CSS
 * from an attribute" helper. Each value is validated for its own shape, so a
 * string arriving from the database cannot turn into a declaration of its own.
 */
export function applyDataStyles(root: ParentNode = document): void {
  for (const element of root.querySelectorAll<HTMLElement>('[data-fill-percent]')) {
    const percent = Number(element.dataset.fillPercent);
    if (!Number.isFinite(percent)) continue;
    element.style.width = `${String(Math.min(100, Math.max(0, percent)))}%`;
  }

  for (const element of root.querySelectorAll<HTMLElement>('[data-border-color]')) {
    const color = element.dataset.borderColor ?? '';
    if (!/^#[0-9a-f]{6}$/i.test(color)) continue;
    element.style.setProperty('border-left-color', color);
  }

  /*
   * A picture Discord hosts, as the background of a banner.
   *
   * Validated to Discord's own CDN and to a plain path: the address comes from
   * an API answer rather than from a member, but a background is one `url()`
   * away from being a request to anywhere, and a rule that only accepts one
   * host cannot be argued with.
   */
  for (const element of root.querySelectorAll<HTMLElement>('[data-background-image]')) {
    const address = element.dataset.backgroundImage ?? '';
    if (!/^https:\/\/cdn\.discordapp\.com\/[\w./?=&-]+$/.test(address)) continue;

    element.style.backgroundImage = `url("${address}")`;
    element.style.backgroundSize = 'cover';
    element.style.backgroundPosition = 'center';
  }
}
